import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PriceHistoryModal = ({ isOpen, onClose, orgId, sku }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      setError('');
      setRows([]);
      if (!isOpen || !orgId || !sku) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('epos_price_history')
          .select('id, changed_at, old_price, new_price, source, invoice_id, changed_by, user_profiles(full_name, email)')
          .eq('org_id', orgId)
          .eq('sku', sku)
          .order('changed_at', { ascending: false })
          .limit(100);
        if (error) throw error;
        setRows(data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load price history');
      } finally { setLoading(false); }
    };
    load();
  }, [isOpen, orgId, sku]);

  const title = useMemo(() => `Price History — ${sku || ''}`.trim(), [sku]);

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="History" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              <p className="text-xs text-muted-foreground">Latest changes to EPOS each-price</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          {error && <div className="text-sm text-destructive mb-2">{error}</div>}
          {loading ? (
            <div className="text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No price changes found for this SKU.</div>
          ) : (
            <div className="overflow-auto border border-border rounded-md">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-muted text-muted-foreground">
                    <Th>Changed At</Th>
                    <Th className="text-right">Old £</Th>
                    <Th className="text-right">New £</Th>
                    <Th className="text-right">Δ</Th>
                    <Th>By</Th>
                    <Th>Source</Th>
                    <Th>Invoice</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const oldP = toNum(r?.old_price);
                    const newP = toNum(r?.new_price);
                    const diff = (oldP != null && newP != null) ? (newP - oldP) : null;
                    const u = r?.user_profiles;
                    const by = u?.full_name || u?.email || r?.changed_by || '';
                    return (
                      <tr key={r.id} className="border-t border-border">
                        <Td>{fmtDateTime(r?.changed_at)}</Td>
                        <Td num>{fmtMoney(oldP)}</Td>
                        <Td num>{fmtMoney(newP)}</Td>
                        <Td num>
                          {diff != null ? (
                            <span className={`inline-flex items-center gap-1 ${diff >= 0 ? 'text-success' : 'text-destructive'}`}>
                              <Icon name={diff >= 0 ? 'ArrowUpRight' : 'ArrowDownRight'} size={14} />
                              {diff > 0 ? '+' : ''}{fmtMoney(diff)}
                            </span>
                          ) : ''}
                        </Td>
                        <Td>{by}</Td>
                        <Td>{r?.source || ''}</Td>
                        <Td mono title={r?.invoice_id || ''}>{shortId(r?.invoice_id)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document?.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-[11px]' : ''} ${num ? 'text-right' : ''}`}>{children}</td>
);

function toNum(v) { if (v === undefined || v === null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function fmtMoney(v) { if (v == null) return ''; const n = Number(v); if (!Number.isFinite(n)) return ''; return `£${n.toFixed(2)}`; }
function fmtDateTime(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return iso || ''; return d.toLocaleString('en-GB'); } catch { return iso || ''; } }
function shortId(id) { if (!id) return ''; const s = String(id); return s.length > 8 ? `${s.slice(0,4)}…${s.slice(-4)}` : s; }

export default PriceHistoryModal;
